import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@/components/button";
import { IconButton } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useMediaQuery } from "@mui/material";
import { Close } from "@mui/icons-material";
import PreLoader from "@/components/preLoader";
import ProductCardForMobile from "./ProductCardForMobile";
import ProductCardForDesktop from "./ProductCardForDesktop";
import OtherStoreProducts from "./OtherStoreProducts";

import { useMutation } from "@apollo/client";
import { UPDATE_PRODUCTS } from "@/services/productService";

import { selectLocalization } from "@/store/store/selectors";

import { Colors } from "@/theme";
import useStyles from "./styles";
import { getStoreAddressString } from "@/utils/otherUtil";
import { STORE_INTEGRATION_TYPE } from "@/constants/config";
import { MERCHANT_ID } from "@/constants/env";
import { calcPriceFromGrossPrice } from "@/utils/priceUtil";

const FUEL_TAXES = [
  {
    id: "432e5ad8-ddd3-4f46-889f-f1fe5f66d1ab",
    name: "VAT",
    rate: 20,
  },
];
const UpdatePriceModal = ({
  open,
  onClose,
  store,
  products,
  otherStoresProducts,
  onUpdateSuccess,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const localization = useSelector(selectLocalization);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  const ProductCard = isDesktop ? ProductCardForDesktop : ProductCardForMobile;
  const memoizedNewPrices = useRef(
    products.map((product) => product.prices?.[0]?.gross_price)
  );
  const [newPrices, setNewPrices] = useState(memoizedNewPrices.current);

  const hasPricesChanged = !!newPrices?.find(
    (price, id) =>
      parseFloat(memoizedNewPrices.current[id]) !== parseFloat(price)
  );

  const [updateProductsMutation, { loading }] = useMutation(UPDATE_PRODUCTS, {
    context: {
      headers: {
        merchantId: MERCHANT_ID,
        storeId: store.id,
      },
    },
    onCompleted: (data) => {
      onUpdateSuccess();
      onClose();
    },
  });

  const onChangePrice = (id, newPrice) => {
    setNewPrices(
      newPrices.map((price, index) => (index === id ? newPrice : price))
    );
  };
  const onUpdate = () => {
    updateProductsMutation({
      variables: {
        products: products
          ?.map((product, id) =>
            parseFloat(memoizedNewPrices.current[id]) ===
            parseFloat(newPrices[id]) // send updated products only
              ? null
              : {
                  id: product.id,
                  prices: [
                    {
                      price: calcPriceFromGrossPrice(newPrices[id], FUEL_TAXES),
                      price_type_id: product.prices?.[0]?.price_type_id,
                      tax_ids: FUEL_TAXES.map((item) => item.id),
                    },
                  ],
                }
          )
          ?.filter((product) => !!product),
      },
    });
  };

  return (
    <>
      <Dialog
        className={classes.container}
        fullWidth
        open={open}
        onClose={onClose}
      >
        <DialogTitle className={classes.title}>
          {t("manage_price_screen.update_price")}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
          }}
        >
          <Close fontSize="large" sx={{ color: Colors.GRAY_DARK }} />
        </IconButton>
        <DialogContent className={classes.content}>
          <Box display={"flex"} justifyContent={"center"} alignItems={"center"}>
            <img src={store.brand?.logo?.thumbnail} width={80} height={80} />
            <Box ml={4}>
              <Box textAlign={"center"} mb={3}>
                <span className={classes.storeNameText}>
                  {`${store.name} (${
                    store.integrations?.find(
                      (item) =>
                        item.integration_type ===
                        STORE_INTEGRATION_TYPE.LIVE_PRICE
                    )?.integration_id
                  })`}
                </span>
              </Box>
              <Box textAlign={"center"} maxWidth={220} margin={"auto"}>
                <span className={classes.addressText}>
                  {getStoreAddressString(store.address)}
                </span>
              </Box>
            </Box>
          </Box>

          <Grid container component={"main"} my={10}>
            <Grid
              item
              xs={12}
              md={6}
              pr={isDesktop ? 7 : 0}
              borderRight={
                isDesktop ? `1px solid ${Colors.GRAY_BORDER}` : "none"
              }
            >
              {isDesktop && (
                <Grid container component={"main"}>
                  <Grid item xs={2} />
                  <Grid item xs={3.5}>
                    <span className={classes.labelText}>
                      {t("manage_price_screen.current_price_margin")}
                    </span>
                  </Grid>
                  <Grid item xs={3.5}>
                    <span className={classes.labelText}>
                      {t("manage_price_screen.previous_price_margin")}
                    </span>
                  </Grid>
                  <Grid item xs={3}>
                    <span className={classes.labelText}>
                      {t("manage_price_screen.new_price")}
                    </span>
                  </Grid>
                </Grid>
              )}
              {products.map((product, id) => (
                <Box key={`product-${id}`}>
                  <ProductCard
                    isLast={id === products.length - 1}
                    localization={localization}
                    product={product}
                    newPrice={newPrices[id]}
                    onChangePrice={(newPrice) => onChangePrice(id, newPrice)}
                  />
                </Box>
              ))}
              <Box>
                <Box display={"flex"} justifyContent={"flex-end"} my={10}>
                  <Button
                    disabled={!hasPricesChanged}
                    color="primary"
                    variant="contained"
                    sx={{ mt: 4 }}
                    className={classes.updateButton}
                    onClick={onUpdate}
                  >
                    {t("manage_price_screen.update_price")}
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              pl={isDesktop ? 7 : 0}
              height={"60vh"}
              overflow={"auto"}
            >
              <OtherStoreProducts otherStoresProducts={otherStoresProducts} />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {loading && <PreLoader size={40} />}
    </>
  );
};

export default UpdatePriceModal;
